/**
 * GPS status taxonomy.
 *
 * Jira exposes a granular `status` per ticket plus a rolled-up `status_category`
 * (NEW | INDETERMINATE | DONE). The app presents 3 client-facing families:
 *   NEW           → « Reçu »
 *   INDETERMINATE → « En cours »
 *   DONE          → « Clôturé »
 *
 * This module is the single source of truth for that mapping so the status
 * correspondence (and any status-category grouping) can be reused everywhere.
 */

export const STATUS_CATEGORY_KEYS = ['NEW', 'INDETERMINATE', 'DONE'];

export const STATUS_FAMILIES = [
  {
    key: 'NEW',
    label: 'Nouveau',
    color: '#2f6df0',
    wrap: 'border-[#cfe0fb] bg-[#f5f9ff] dark:border-blue-900 dark:bg-blue-950/30',
    chip: 'text-[#3d5a8a] dark:text-blue-200 bg-[#e6f0ff] dark:bg-blue-900/40',
  },
  {
    key: 'INDETERMINATE',
    label: 'En cours',
    color: '#f5a623',
    wrap: 'border-[#f4e2c4] bg-[#fffaf1] dark:border-amber-900 dark:bg-amber-950/30',
    chip: 'text-[#8a6213] dark:text-amber-200 bg-[#fdf0d8] dark:bg-amber-900/40',
  },
  {
    key: 'DONE',
    label: 'Clôturé',
    color: '#16a866',
    wrap: 'border-[#c7e8d5] bg-[#f4fbf7] dark:border-green-900 dark:bg-green-950/30',
    chip: 'text-[#15784c] dark:text-green-200 bg-[#e2f5ea] dark:bg-green-900/40',
  },
];

/**
 * "Brouillon" (draft) statuses are unfinished tickets. They are excluded from
 * the Général tab analytics (KPIs, charts, evolution, aging) but remain visible
 * in the Secteurs and Tickets tabs. Single source of truth for the predicate.
 */
const DRAFT_STATUS_TOKENS = ['brouillon', 'draft'];

export const isDraftIssue = (issue) => {
  const s = (issue?.status || '').trim().toLowerCase();
  return DRAFT_STATUS_TOKENS.some((t) => s.includes(t));
};

export const excludeDrafts = (issues = []) => (issues || []).filter((i) => !isDraftIssue(i));

/**
 * "Annulés" bucket: tickets closed without resolution — cancelled, rejected or
 * declined. Single source of truth so the KPI, the evolution series and the
 * Secteurs column stay aligned.
 */
const ABANDONED_STATUS_TOKENS = ['annul', 'rejet', 'declin'];

export const isAbandonedIssue = (issue) => {
  const s = (issue?.status || '').trim().toLowerCase();
  return ABANDONED_STATUS_TOKENS.some((t) => s.includes(t));
};

/**
 * "Definition of Done" — a genuinely RESOLVED ticket, across every project
 * workflow. Intervention-type tickets finish at "Terminé"/"Terminé(e)";
 * request-type tickets (e.g. project DPGP) finish at "Approuvé". Both count as
 * resolved. Everything else in the DONE category (Annulé, Rejeté, HORS SCOPE,
 * Declined, ARCHIVED) and the intermediate "Intervention effectuée" are NOT
 * resolved. Single source of truth for SLAs, Coûts and any "résolu" metric.
 */
const RESOLVED_STATUS_TOKENS = ['termin', 'approuv'];

export const isResolvedIssue = (issue) => {
  const s = (issue?.status || '').trim().toLowerCase();
  return RESOLVED_STATUS_TOKENS.some((t) => s.includes(t));
};

/**
 * "Interventions" bucket: a completed on-site intervention. Title-agnostic and
 * deliberately simple — a "Terminé"/"Terminé(e)" status WITH a scheduled due date.
 * The due date is the real discriminator: anything that is not an intervention
 * has no duedate. Count is deduplicated per store × day (see
 * `uniqueInterventionsByStoreDay`): 2-3 tickets for the same store on the same day
 * = one intervention.
 */
export const isInterventionDoneIssue = (issue) => {
  const status = (issue?.status || '').trim().toLowerCase();
  return !!issue?.duedate && status.includes('termin');
};

/**
 * Collapse interventions to one per (intervention_point, due-date day). `dayOf`
 * maps a raw duedate to a YYYY-MM-DD key (the caller supplies it so date parsing
 * stays in one place). Returns the kept issues (first seen per key).
 */
export const uniqueInterventionsByStoreDay = (issues = [], dayOf) => {
  const seen = new Set();
  return (issues || []).filter((i) => {
    const key = `${i?.intervention_point || ''}__${dayOf(i?.duedate)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Unique draft status labels present in `issues` (sorted, FR). */
export const getDraftStatuses = (issues = []) => {
  const set = new Set();
  (issues || []).forEach((i) => {
    if (isDraftIssue(i) && i?.status) set.add(i.status.trim());
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
};

/**
 * Resolve an issue's status family. Prefers the explicit `status_category`;
 * falls back to keyword heuristics on the raw `status` when missing.
 * Always returns one of STATUS_CATEGORY_KEYS.
 */
export const normalizeStatusCategory = (issue) => {
  const raw = (issue?.status_category || issue?.statusCategory || '').trim().toUpperCase();
  if (STATUS_CATEGORY_KEYS.includes(raw)) return raw;

  const status = (issue?.status || '').toLowerCase();
  if (
    status.includes('termin') ||
    status.includes('aband') ||
    status.includes('annul') ||
    status.includes('résolu') ||
    status.includes('resolu') ||
    status.includes('fermé') ||
    status.includes('ferme') ||
    status.includes('clôtur') ||
    status.includes('clotur')
  ) {
    return 'DONE';
  }
  if (
    status.includes('attent') ||
    status.includes('en cours') ||
    status.includes('progress') ||
    status.includes('trait') ||
    status.includes('planif') ||
    status.includes('intervention') ||
    status.includes('standby')
  ) {
    return 'INDETERMINATE';
  }
  return 'NEW';
};

/**
 * Group the unique status labels present in `issues` by their family.
 * Returns { NEW: string[], INDETERMINATE: string[], DONE: string[] } (sorted).
 */
export const groupStatusesByCategory = (issues = []) => {
  const sets = { NEW: new Set(), INDETERMINATE: new Set(), DONE: new Set() };
  (issues || []).forEach((issue) => {
    const status = (issue?.status || '').trim();
    if (!status) return;
    if (isDraftIssue(issue)) return; // drafts are listed apart (excluded from analytics)
    const cat = normalizeStatusCategory(issue);
    sets[cat].add(status);
  });
  const sortFr = (a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' });
  return {
    NEW: Array.from(sets.NEW).sort(sortFr),
    INDETERMINATE: Array.from(sets.INDETERMINATE).sort(sortFr),
    DONE: Array.from(sets.DONE).sort(sortFr),
  };
};
